import React, { useEffect, useRef } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import 'styles/cssImports';
import {
  unstable_HistoryRouter as HistoryRouter,
  Routes,
  Route,
  Navigate,
  useParams,
  useLocation,
  useMatch,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import { NotFound } from 'pages/Popups/components/NotFound';
import { Maintenance } from 'pages/Popups/components/Maintenance';
import { LoginDep } from './pages/Login';
import ChangePassword from './pages/Login/ChangePassword';
import Onboard from './pages/Login/Onboard';
import { Collections } from './pages/Collection/ListPage';
import { Collection } from './pages/Collection/ViewPage';
import Share from './pages/Share/SharePage';
import Popups from './pages/Popups';
import WidgetList from './pages/Widget/ListPage';
import WidgetEdit from './pages/Widget/EditPage';
import EmbedHTML from './pages/Widget/Edit/EmbedHTML';
import { WidgetCreate } from './pages/Widget/CreateModal';
import { Oauth } from './pages/Oauth';
import { history } from './browserHistory';
import { Services } from './services';
import { ROOT_PATH } from './config';
import { Start } from './types/Start';
import { ServiceName } from './types/Services';
import { DetailedMediaView } from './pages/Media/DetailedMediaView';
import {
  ArgsName,
  Pathname,
  PathAction,
  QsNames,
} from './types/Browsing';
import { queryClient } from './query';
import { ProductPage } from './pages/Product/ProductPage';
import { OnQsMatch } from './components/OnQsMatch';
import GetRightsQueryStringDetector from './pages/Media/GetRights/GetRightsQueryStringDetector';
import DiscoverController from './pages/Discover/DiscoverController';
import { MediaDownloadController } from './pages/Media/Download/DownloadController';
import { AlertSnackbar } from './fragments/Alerting/AlertSnackbar';
import { AnalyzePage } from './pages/Analyze/AnalyzePage';
import { CRMPage } from '@src/pages/CRM/CRMPage';

// Inject the login service once at module load time
const LoginPage = LoginDep(Services);

function CollectionRouted() {
  const { [ArgsName.collect.id]: id } = useParams<Record<string, string>>();
  return <Collection services={Services} collectionId={id!} />;
}

function WidgetEditRouted() {
  const { [ArgsName.widget.id]: id } = useParams<Record<string, string>>();
  return <WidgetEdit widgetId={id!} />;
}

function WidgetCreateRouted() {
  const navigate = useNavigate();
  return (
    <WidgetCreate
      onClose={() => navigate(Pathname.widget)}
      onCreated={(widget) => navigate(`${Pathname.widget}/${widget._id}`)}
    />
  );
}

function EmbedHTMLRouted() {
  const [searchParams] = useSearchParams();
  const widgetId = searchParams.get('widgetId') || '';
  return <EmbedHTML widgetId={widgetId} />;
}

/** Renders the media detail modal when the detailedMedia QS param is present. */
function DetailedMediaViewModal() {
  const match = useMatch(`/collections/:${ArgsName.collect.id}`);
  const location = useLocation();
  return (
    <OnQsMatch qsKey={QsNames.detailedMediaView} match={(val) => !!val.length}>
      <DetailedMediaView
        collectionId={match?.params[ArgsName.collect.id]}
        currentPathname={location.pathname}
        services={Services}
      />
    </OnQsMatch>
  );
}

export function Root() {
  const startInit = useRef(false);

  useEffect(() => {
    if (!startInit.current) {
      startInit.current = true;
      Services.get<Start>(ServiceName.Start).init();
    }
  }, []);

  useEffect(() => {
    const handleWheel = (e: Event) => {
      if ((e as WheelEvent).deltaX !== 0) e.preventDefault();
    };
    const handleTouchStart = (e: TouchEvent) => e.preventDefault();
    const handleTouchMove = (e: TouchEvent) => e.preventDefault();

    document.body.addEventListener('mousewheel', handleWheel);
    document.addEventListener('touchstart', handleTouchStart, { passive: false });
    document.addEventListener('touchmove', handleTouchMove, { passive: false });

    return () => {
      document.body.removeEventListener('mousewheel', handleWheel);
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
    };
  }, []);

  return (
    <HistoryRouter history={history as any}>
      <QueryClientProvider client={queryClient}>
        <div style={{ height: '100%', width: '100%', position: 'absolute' }}>
          <AlertSnackbar />
          <Popups services={Services} />
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/changepassword" element={<ChangePassword />} />
            <Route path="/onboard" element={<Onboard />} />
            <Route path="/oauth/:status" element={<Oauth />} />
            <Route path="/" element={<Navigate to={ROOT_PATH} replace />} />
            <Route path={Pathname.discover} element={<DiscoverController services={Services} />} />
            <Route path={Pathname.product} element={<ProductPage services={Services} />} />
            <Route path={Pathname.analyze} element={<AnalyzePage />} />
            <Route path={Pathname.collect} element={<Collections />} />
            <Route path={`/collections/:${ArgsName.collect.id}`} element={<CollectionRouted />} />
            <Route path="/share" element={<Share />} />
            <Route path={Pathname.widget} element={<WidgetList services={Services} />} />
            <Route path={`${Pathname.widget}/${PathAction.create}`} element={<WidgetCreateRouted />} />
            <Route path={`${Pathname.widget}/:${ArgsName.widget.id}`} element={<WidgetEditRouted />} />
            <Route path="/widget-embed-html" element={<EmbedHTMLRouted />} />
            <Route path="/crm" element={<CRMPage services={Services} />} />
            <Route
              path={`/crm/:${ArgsName.conversation.targetId}/:${ArgsName.conversation.interlocutorId}/:${ArgsName.conversation.conversationId}`}
              element={<CRMPage services={Services} />}
            />
            <Route path="/maintenance" element={<Maintenance />} />
            <Route path="*" element={<NotFound />} />
          </Routes>

          {/* Modal overlays — rendered on every route, shown only when their QS params are present */}
          <DetailedMediaViewModal />
          <OnQsMatch qsKey={QsNames.getRightsModal} match={(val) => !!val.length}>
            <GetRightsQueryStringDetector services={Services} />
          </OnQsMatch>
          <OnQsMatch qsKey={QsNames.mediaDownload} match={(val) => val.length > 0}>
            <MediaDownloadController browsingService={Services.get(ServiceName.Browsing)} />
          </OnQsMatch>
        </div>
      </QueryClientProvider>
    </HistoryRouter>
  );
}
